import { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import Image from 'common/image'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
// import required modules
import { Navigation } from 'swiper/modules'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
//Import own swiper css
import './my-own-swiper.css'
import { adobeInteractionEvent } from 'utils/adobewa'

const WhatIsNewContainer = styled.div`
    width: 100%;
    height: 570px;
    background-color: #efeade;
`
const TitleContainer = styled.div`
    margin-top: 48px;
    margin-bottom: 24px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
`

const TitleText = styled.div`
    color: #121b21;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: 40px;
`

const CustomSwiperSlide = styled(SwiperSlide)`
    width: 1040px;
    padding: 31px 33px;
    border-radius: 30px;
    background-color: white;
`

const SwiperSlideLeft = styled.div`
    width: 438px;
    padding: 0px 32px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`
const SwiperSlideTitle = styled.div`
    color: #222;
    font-size: 14px;
    font-weight: bold;
    line-height: 20px; /* 175% */
    margin-bottom: 12px;
`
const SwiperSlideDescription = styled.div`
    color: #d2a461;
    font-size: 18px;
    line-height: 25px;
`
const SwiperSlideButton = styled.div`
    margin-top: 12px;
    width: 212px;
    border: 1px solid #d2a461;
    padding: 5px 30px;
    border-radius: 26px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`

const SwiperSlideButtonText = styled.div`
    color: #d2a461;
    font-size: 16px;
    font-weight: bold;
`

const SwiperSlideRight = styled.div`
    width: 536px;
`
const SwiperSlideframe = styled.div`
    display: flex;
    align-items: center;
`

export default function WhatIsNewSwiper() {
    const { i18n, t } = useTranslation('', { keyPrefix: 'homepage.whatisnew' })
    const lang = i18n.language
    const configs = useSelector((state: any) => state.config.configs)
    const banners = useMemo(
        () => configs?.homepage?.carouselBanners || [],
        [configs]
    )

    const sortedBanners = [...banners].sort(function (a: any, b: any) {
        return a.id - b.id || a.titleEn.localeCompare(b.titleEn)
    })

    const [bannerAppeared, setBannerAppeared] = useState<number[]>([])
    const [swiperKey, setSwiperKey] = useState(0)

    useEffect(() => {
        // Check if the Swiper instance exists and then call update()
        setSwiperKey((prevKey) => prevKey + 1)
    }, [banners])

    const redirectTo = (carouselBanner: any) => {
        const targetTitle =
            lang === 'zh' ? carouselBanner?.titleZh : carouselBanner?.titleEn
        const targetUrl =
            lang === 'zh'
                ? carouselBanner.destinationZh
                : carouselBanner.destinationEn

        adobeInteractionEvent({
            lang: lang,
            eventID: 'prioritysite_clickBanner_Home_WhatsNew',
            contentTitle: targetTitle,
            contentUrl: targetUrl,
        })

        window.open(targetUrl, '_blank')
    }

    const fireActiveBannerWAEvent = (index: number) => {
        const targetTitle =
            lang === 'zh' ? banners[index]?.titleZh : banners[index]?.titleEn
        const targetUrl =
            lang === 'zh'
                ? banners[index]?.destinationZh
                : banners[index]?.destinationEn
        if (!bannerAppeared.includes(index)) {
            adobeInteractionEvent({
                lang: lang,
                eventID: 'prioritysite_BannerImpression_Home_WhatsNew',
                contentTitle: targetTitle,
                contentUrl: targetUrl,
            })
            const newArray: number[] = bannerAppeared
            newArray.push(index)
            setBannerAppeared(newArray)
        }
    }

    return (
        <WhatIsNewContainer>
            <TitleContainer>
                <TitleText>{t(`WHATNEW`)}</TitleText>
            </TitleContainer>
            <div className="swiper-container-wrapper">
                <Swiper
                    key={swiperKey}
                    initialSlide={0}
                    loop={true}
                    centeredSlides={true}
                    slidesPerView={'auto'}
                    spaceBetween={60}
                    modules={[Navigation]}
                    navigation={{
                        nextEl: '.custom-swiper-button-next',
                        prevEl: '.custom-swiper-button-prev',
                    }}
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                    }}
                    speed={1000} // Adjust speed for desired effect
                    onTransitionEnd={(swiper: any) =>
                        fireActiveBannerWAEvent(swiper?.realIndex)
                    }
                >
                    {sortedBanners?.map(
                        (carouselBanner: any, index: number) => {
                            return (
                                <CustomSwiperSlide key={index}>
                                    <SwiperSlideframe>
                                        <SwiperSlideLeft>
                                            <SwiperSlideTitle>
                                                {lang === 'zh'
                                                    ? carouselBanner.titleZh
                                                    : carouselBanner.titleEn}
                                            </SwiperSlideTitle>
                                            <SwiperSlideDescription
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        lang === 'zh'
                                                            ? carouselBanner.descriptionZh
                                                            : carouselBanner.descriptionEn,
                                                }}
                                            ></SwiperSlideDescription>
                                            {(lang === 'en' &&
                                                carouselBanner.buttonTextEn &&
                                                carouselBanner.destinationEn) ||
                                            (lang === 'zh' &&
                                                carouselBanner.buttonTextZh &&
                                                carouselBanner.destinationZh) ? (
                                                <SwiperSlideButton
                                                    onClick={() =>
                                                        redirectTo(
                                                            carouselBanner
                                                        )
                                                    }
                                                >
                                                    <SwiperSlideButtonText>
                                                        {lang === 'zh'
                                                            ? carouselBanner.buttonTextZh
                                                            : carouselBanner.buttonTextEn}
                                                    </SwiperSlideButtonText>
                                                </SwiperSlideButton>
                                            ) : null}
                                        </SwiperSlideLeft>
                                        <SwiperSlideRight>
                                            <Image
                                                src={
                                                    lang === 'zh'
                                                        ? carouselBanner.imageZh
                                                        : carouselBanner.imageEn
                                                }
                                                width={539}
                                                height={270}
                                                borderradius={30}
                                                alt="Carousel Banner"
                                            />
                                        </SwiperSlideRight>
                                    </SwiperSlideframe>
                                </CustomSwiperSlide>
                            )
                        }
                    )}
                </Swiper>
                <div className="custom-swiper-button-prev" />
                <div className="custom-swiper-button-next" />
            </div>
        </WhatIsNewContainer>
    )
}
